import React, { useCallback, useEffect, useRef, useState } from "react";
import apiClient, { fetchSystemInfo, triggerCollector } from "../api/client";
import { useRoles } from "../auth/access";
import type {
  CollectorStatusEntry,
  ConnectivityCheck,
  SystemInfoResponse,
  TableStatEntry,
} from "../types";
import { useDateFormat } from "../preferences/PreferencesProvider";
import { formatDateTime } from "../utils/datetime";
import DetailModal from "../components/DetailModal";

// ---------------------------------------------------------------------------
// Helper: age formatting
// ---------------------------------------------------------------------------

function formatAge(minutes?: number): string {
  if (minutes === undefined || minutes === null) return "—";
  if (minutes < 2) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  if (rem === 0) return `${hours}h ago`;
  return `${hours}h ${rem}m ago`;
}

function formatBytes(bytes?: number): string {
  if (bytes === undefined || bytes === null) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Collector COLLECT_TYPE options
// ---------------------------------------------------------------------------

const COLLECT_TYPES = [
  "workspaces",
  "activity",
  "ual",
  "privileges",
  "artifacts",
  "permissions",
  "pipelines",
  "scanner",
  "refresh",
  "refresh_schedules",
  "apps",
  "subscriptions",
  "imports",
  "dataflows",
  "pim",
  "security_alerts",
  "unused_artifacts",
];

// ---------------------------------------------------------------------------
// Status icons
// ---------------------------------------------------------------------------

function StatusIcon({ status }: { status: string }) {
  if (status === "ok") {
    return (
      <svg className="w-5 h-5 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
    );
  }
  if (status === "warning") {
    return (
      <svg className="w-5 h-5 text-yellow-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      </svg>
    );
  }
  if (status === "error") {
    return (
      <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    );
  }
  return (
    <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function statusBadgeClass(status: string): string {
  if (status === "ok") return "bg-green-100 text-green-800";
  if (status === "warning") return "bg-yellow-100 text-yellow-800";
  if (status === "error") return "bg-red-100 text-red-800";
  return "bg-gray-100 text-gray-600";
}

function collectorRowClass(status: string): string {
  if (status === "ok") return "text-green-700";
  if (status === "warning") return "text-yellow-700";
  if (status === "error") return "text-red-700";
  if (status === "never_run") return "text-gray-500";
  return "text-gray-400";
}

function statusLabel(status: string): string {
  return status === "never_run" ? "Never run" : status.toUpperCase();
}

// Worst-first ordering for the attention panel, so the thing most likely to
// matter is the thing read first.
const STATUS_SEVERITY: Record<string, number> = {
  error: 3,
  never_run: 2,
  warning: 2,
  unknown: 1,
  ok: 0,
};

function severity(status: string): number {
  return STATUS_SEVERITY[status] ?? 1;
}

// ---------------------------------------------------------------------------
// Check card
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Auto-fix — checks that the app can remediate itself, pending user approval.
// The click is the approval; the backend logs who requested it.
// ---------------------------------------------------------------------------

interface AutoFix {
  label: string;
  confirm: string;
  endpoint: string; // POST; returns [{name, status, detail}]
}

const AUTO_FIXES: Record<string, AutoFix> = {
  "Collector Access — Monitored Workspaces": {
    label: "Fix now — grant collector access",
    confirm:
      "Grant the collector service principal Viewer access on every monitored workspace? " +
      "This calls the Power BI admin API on your behalf and is logged.",
    endpoint: "/api/workspaces/ensure-access-all",
  },
};

interface FixResult {
  workspaceId?: string;
  name: string;
  status: string;
  detail: string;
}

function AutoFixButton({ fix, onDone }: { fix: AutoFix; onDone: () => void }) {
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<FixResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    if (!window.confirm(fix.confirm)) return;
    setRunning(true);
    setError(null);
    try {
      const resp = await apiClient.post<FixResult[]>(fix.endpoint);
      setResults(resp.data);
      onDone();
    } catch (err: any) {
      setError(err?.message ?? "Auto-fix failed.");
    } finally {
      setRunning(false);
    }
  };

  const fixed = results?.filter((r) => ["ok", "granted"].includes(r.status)).length ?? 0;

  return (
    <div className="mt-1 space-y-1.5">
      <button
        onClick={run}
        disabled={running}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50"
      >
        {running ? (
          <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
        ) : (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085" />
          </svg>
        )}
        {running ? "Fixing…" : fix.label}
      </button>
      {error && <p className="text-xs text-red-600">{error}</p>}
      {results && (
        <div className="text-xs space-y-0.5">
          <p className={fixed === results.length ? "text-green-700 font-medium" : "text-amber-700 font-medium"}>
            {fixed} of {results.length} workspace(s) fixed.
          </p>
          {results.filter((r) => !["ok", "granted"].includes(r.status)).map((r) => (
            <p key={r.workspaceId ?? r.name} className="text-gray-500 break-words">
              <span className="font-medium text-gray-700">{r.name}</span>: {r.status} — {r.detail}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function CheckCard({ check, onRefresh }: { check: ConnectivityCheck; onRefresh?: () => void }) {
  const [detailOpen, setDetailOpen] = useState(false);
  // Only offer the fix when the backend says it can actually succeed. It
  // pre-flights the prerequisite (for the collector grant, a delegated
  // Fabric-admin token), so a blocked fix shows what to do instead of a
  // button that fails every time.
  const candidateFix = check.status !== "ok" ? AUTO_FIXES[check.name] : undefined;
  const autoFix = check.can_auto_fix === false ? undefined : candidateFix;
  const blocked = candidateFix && check.can_auto_fix === false;
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-2 shadow-sm">
      <div className="flex items-start gap-2">
        <StatusIcon status={check.status} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate" title={check.name}>{check.name}</p>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(check.status)}`}>
              {check.status.toUpperCase()}
            </span>
            {check.response_time_ms !== undefined && check.response_time_ms !== null && (
              <span className="text-xs text-gray-400">{check.response_time_ms}ms</span>
            )}
          </div>
        </div>
      </div>
      <p className="text-xs text-gray-600">{check.message}</p>
      {autoFix && onRefresh && <AutoFixButton fix={autoFix} onDone={onRefresh} />}
      {blocked && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2">
          {/* This heading used to assert "a tenant administrator must act
              first" for every blocked fix. It sits directly beneath the
              failure message, so it read as the *cause* of the failure — and
              it named the wrong person for most of the reasons that land here.
              An expired service-account password is a Secrets Manager
              rotation, not a tenant consent, and nothing a tenant
              administrator does will fix it. The reason below says who and
              what; the heading now only says what it is about. */}
          <p className="text-xs font-medium text-amber-900">
            No automatic fix for this — why:
          </p>
          <p className="text-xs text-amber-800 mt-0.5">{check.fix_blocked_reason}</p>
          <p className="text-xs text-amber-700 mt-1">
            This explains the missing “Fix now” button, not necessarily what
            made the check fail. Open “Details” for that.
          </p>
        </div>
      )}
      {/* The detail used to unfold inside the card. These cards sit in a
          responsive grid of nearly seventy, so expanding one grew its row and
          shoved every card after it — and the detail is the longest text on
          the page, often an unbroken ARN or exception, which made the card it
          was in several times the height of its neighbours. It opens in a
          dialog now, where it has room and the grid stays still. */}
      {check.detail && (
        <div>
          <button
            onClick={() => setDetailOpen(true)}
            className="text-xs text-blue-600 hover:underline"
          >
            Details
          </button>
        </div>
      )}
      {detailOpen && check.detail && (
        <DetailModal
          title={check.name}
          subtitle={check.message}
          width="lg"
          onClose={() => setDetailOpen(false)}
        >
          <p className="whitespace-pre-wrap break-words text-sm text-content-muted">
            {check.detail}
          </p>
        </DetailModal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function SkeletonCard() {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse">
      <div className="flex gap-2 mb-2">
        <div className="w-5 h-5 bg-gray-200 rounded-full" />
        <div className="h-4 bg-gray-200 rounded w-1/2" />
      </div>
      <div className="h-3 bg-gray-100 rounded w-3/4" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function InfoPage() {
  const dateFormat = useDateFormat();
  const formatDate = (value?: string) => (value ? formatDateTime(value, dateFormat) : "Never");

  const [data, setData] = useState<SystemInfoResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [secondsSince, setSecondsSince] = useState(0);

  const [triggerType, setTriggerType] = useState(COLLECT_TYPES[0]);
  const [triggering, setTriggering] = useState(false);
  const [triggerMessage, setTriggerMessage] = useState<string | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { isAdmin } = useRoles();

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    setSecondsSince(0);
    fetchSystemInfo()
      .then((d) => setData(d))
      .catch((e) => setError(e.message ?? "Failed to load system info."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Tick counter
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setSecondsSince((s) => s + 1);
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [data]);

  const handleTrigger = async () => {
    setTriggering(true);
    setTriggerMessage(null);
    try {
      const result = await triggerCollector(triggerType);
      if (result.triggered) {
        setTriggerMessage(`Triggered "${triggerType}" — Lambda invocation status ${result.status_code}`);
      } else {
        setTriggerMessage("Trigger returned unexpected response.");
      }
    } catch (e) {
      const err = e as Error;
      setTriggerMessage(`Error: ${err.message}`);
    } finally {
      setTriggering(false);
    }
  };

  // Partition checks
  const msftChecks = data?.checks.filter((c) => c.category === "microsoft_api") ?? [];
  const dynamoChecks = data?.checks.filter((c) => c.category === "aws" && c.name.startsWith("DynamoDB")) ?? [];
  const s3Checks = data?.checks.filter((c) => c.category === "aws" && c.name.startsWith("S3")) ?? [];
  const secretChecks = data?.checks.filter((c) => c.category === "aws" && c.name.startsWith("Secrets")) ?? [];

  // Everything that needs acting on, gathered above the fold. The page runs
  // several screens deep and the collector table is at the bottom of it, so
  // a collector that stopped running was only ever found by scrolling.
  const failedChecks = (data?.checks.filter((c) => c.status !== "ok") ?? [])
    .slice()
    .sort((a, b) => severity(b.status) - severity(a.status));
  const failedCollectors = (data?.collectorStatus.filter((c) => c.status !== "ok") ?? [])
    .slice()
    .sort((a, b) => severity(b.status) - severity(a.status));
  const issueCount = failedChecks.length + failedCollectors.length;
  const worst =
    [...failedChecks, ...failedCollectors].some((c) => c.status === "error")
      ? "error"
      : "warning";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Status</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Connectivity diagnostics for all data sources and infrastructure.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {!loading && (
            <span className="text-sm text-gray-400">
              Last checked: {secondsSince}s ago
            </span>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="px-4 py-2 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 disabled:opacity-50 transition-colors"
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={load} className="ml-4 text-red-600 underline text-xs">Retry</button>
        </div>
      )}

      {/* Summary row */}
      {(loading || data) && (
        <div className="grid grid-cols-3 gap-4">
          {loading ? (
            <>
              <div className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse h-16" />
              <div className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse h-16" />
              <div className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse h-16" />
            </>
          ) : (
            <>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                <svg className="w-7 h-7 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <p className="text-2xl font-bold text-green-700">{data!.summary.ok ?? 0}</p>
                  <p className="text-xs text-green-600 font-medium">OK</p>
                </div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center gap-3">
                <svg className="w-7 h-7 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                </svg>
                <div>
                  <p className="text-2xl font-bold text-yellow-700">{data!.summary.warning ?? 0}</p>
                  <p className="text-xs text-yellow-600 font-medium">Warnings</p>
                </div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
                <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                <div>
                  <p className="text-2xl font-bold text-red-700">{data!.summary.error ?? 0}</p>
                  <p className="text-xs text-red-600 font-medium">Errors</p>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Needs attention — every non-OK signal on the page, repeated here so
          that the three counts above are never the whole story a viewer gets
          without scrolling. Absent when everything is healthy. */}
      {!loading && data && issueCount > 0 && (
        <section
          className={`rounded-xl border p-4 ${
            worst === "error" ? "border-red-300 bg-red-50" : "border-yellow-300 bg-yellow-50"
          }`}
        >
          <div className="flex items-start gap-2">
            <StatusIcon status={worst} />
            <div className="flex-1 min-w-0">
              <h2 className="text-base font-semibold text-gray-900">
                Needs attention — {issueCount} of{" "}
                {(data.checks.length + data.collectorStatus.length).toLocaleString()} signals
              </h2>
              <p className="text-xs text-gray-600 mt-0.5">
                {failedCollectors.length > 0 && (
                  <>
                    {failedCollectors.length} collector(s) overdue or stalled
                    {failedChecks.length > 0 && ", "}
                  </>
                )}
                {failedChecks.length > 0 && <>{failedChecks.length} connectivity check(s) failing</>}
                . Detail repeats in the sections below.
              </p>
            </div>
          </div>

          {failedCollectors.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {failedCollectors.map((c: CollectorStatusEntry) => (
                <li
                  key={c.syncType}
                  className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm bg-white/70 border border-gray-200 rounded-lg px-3 py-1.5"
                >
                  <span className={`text-xs font-semibold ${collectorRowClass(c.status)}`}>
                    {statusLabel(c.status)}
                  </span>
                  <span className="font-medium text-gray-800">{c.syncType}</span>
                  <span className="text-xs text-gray-600">
                    {c.ageMinutes === undefined || c.ageMinutes === null
                      ? "no completed pass on record"
                      : `last progress ${formatAge(c.ageMinutes)}`}
                    {c.expectedIntervalMinutes
                      ? ` — expected every ${c.expectedIntervalMinutes} min`
                      : ""}
                    {c.passState === "stalled" && " — pass not finishing"}
                  </span>
                </li>
              ))}
            </ul>
          )}

          {failedChecks.length > 0 && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {failedChecks.map((c) => (
                // Carries the in-card "Fix now" button, so a remediable
                // failure can be remediated from here.
                <CheckCard key={c.name} check={c} onRefresh={isAdmin ? load : undefined} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Microsoft APIs */}
      <section>
        <h2 className="text-base font-semibold text-gray-700 mb-3">Microsoft APIs</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {loading
            ? Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)
            : msftChecks.map((c) => (
                // onRefresh is what enables the in-card "Fix now" buttons, so
                // withholding it for other roles hides an Admin-only action.
                <CheckCard key={c.name} check={c} onRefresh={isAdmin ? load : undefined} />
              ))}
        </div>
      </section>

      {/* Collector Status. Above AWS Infrastructure, which is two dozen rows
          of table inventory nobody reads first. */}
      <section>
        <h2 className="text-base font-semibold text-gray-700 mb-3">Collector Status</h2>
        {loading ? (
          <div className="bg-white border border-gray-200 rounded-xl animate-pulse h-48" />
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Collector</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Last Run</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Age</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Expected Interval</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data?.collectorStatus.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-6 text-center text-gray-400 text-sm">
                      No collector records found in sync-state table.
                    </td>
                  </tr>
                ) : data?.collectorStatus.map((c: CollectorStatusEntry) => (
                  <tr key={c.syncType} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-medium text-gray-800">{c.syncType}</td>
                    <td className="px-4 py-2.5 text-gray-600 text-xs">
                      {formatDate(c.lastSyncedAt)}
                      {(c.passState === "in_progress" || c.passState === "stalled") && (
                        <span
                          className="block text-gray-400"
                          title="This collector spans invocations. The date above is its last completed pass; a later one is still running."
                        >
                          pass still running — cursor moved{" "}
                          {formatDate(c.lastCheckpointAt)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 text-xs">{formatAge(c.ageMinutes)}</td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">
                      {c.expectedIntervalMinutes ? `${c.expectedIntervalMinutes} min` : "—"}
                    </td>
                    <td className={`px-4 py-2.5 text-xs font-semibold ${collectorRowClass(c.status)}`}>
                      {statusLabel(c.status)}
                      {c.passState === "stalled" && (
                        <span className="block font-normal text-gray-500">
                          pass not finishing
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* AWS Infrastructure */}
      <section>
        <h2 className="text-base font-semibold text-gray-700 mb-3">AWS Infrastructure</h2>

        {/* DynamoDB — compact table */}
        <div className="mb-4">
          <h3 className="text-sm font-medium text-gray-600 mb-2">DynamoDB Tables</h3>
          {loading ? (
            <div className="bg-white border border-gray-200 rounded-xl animate-pulse h-48" />
          ) : (
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Table</th>
                    <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">~Items</th>
                    <th className="px-4 py-2.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Size</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data?.tableStats.map((t: TableStatEntry) => (
                    <tr key={t.tableName} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-800 font-mono text-xs">{t.tableName}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass(t.tableStatus === "ACTIVE" ? "ok" : t.tableStatus === "ERROR" ? "error" : "warning")}`}>
                          {t.tableStatus}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right text-gray-600">
                        {t.itemCount !== undefined && t.itemCount !== null ? t.itemCount.toLocaleString() : "—"}
                      </td>
                      <td className="px-4 py-2 text-right text-gray-500 text-xs">{formatBytes(t.tableSizeBytes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Also show per-check status for DynamoDB from checks */}
        {!loading && dynamoChecks.some((c) => c.status !== "ok") && (
          <div className="mb-4">
            <p className="text-xs text-red-600 font-medium mb-2">Tables with issues:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {dynamoChecks.filter((c) => c.status !== "ok").map((c) => (
                <CheckCard key={c.name} check={c} />
              ))}
            </div>
          </div>
        )}

        {/* S3 + Secrets */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)
            : [...s3Checks, ...secretChecks].map((c) => <CheckCard key={c.name} check={c} />)}
        </div>
      </section>

      {/* Manual Trigger. Admin only — triggering a collector acts on the
          tenant, and the API refuses it for other roles. */}
      {isAdmin && (
      <section>
        <h2 className="text-base font-semibold text-gray-700 mb-3">Manual Collector Trigger</h2>
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <p className="text-sm text-gray-600 mb-3">
            Invoke a collector Lambda function immediately (asynchronous — check Collector Status above after ~30 seconds).
          </p>
          <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-2.5 text-xs text-blue-800 mb-4 flex items-start gap-2">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>
              <strong>Manual runs</strong> collect from <strong>all workspaces</strong> (no filter).{" "}
              <strong>Scheduled runs</strong> (EventBridge) collect only from workspaces marked as <em>monitored</em>,
              except tenant-wide sources (activity log, UAL, PIM, privileges) which always cover the full tenant.
            </span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <select
              value={triggerType}
              onChange={(e) => setTriggerType(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              {COLLECT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <button
              onClick={handleTrigger}
              disabled={triggering}
              className="px-5 py-2 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 disabled:opacity-50 transition-colors"
            >
              {triggering ? "Triggering…" : "Run Now"}
            </button>
          </div>
          {triggerMessage && (
            <p className={`mt-3 text-sm ${triggerMessage.startsWith("Error") ? "text-red-600" : "text-green-700"}`}>
              {triggerMessage}
            </p>
          )}
        </div>
      </section>
      )}

      {/* Footer */}
      {data && (
        <p className="text-xs text-gray-400 text-right">
          Report generated: {formatDate(data.generatedAt)}
        </p>
      )}
    </div>
  );
}
